import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorState,
  Heading,
  Loading,
  Row,
  Screen,
  TextField,
  Title,
} from '../components/ui';
import { api, ApiError } from '../lib/api';
import { formatPhone, timezoneOptions } from '../lib/format';
import './customers.css';

/** Customer list and search (§7). */

export interface CustomerSummary {
  id: string;
  name: string;
  phone: string;
  city: string | null;
  timezone: string | null;
  family_cluster_id: string | null;
  family_label: string | null;
}

export default function Customers() {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<CustomerSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async (query: string) => {
    setError(null);
    try {
      const res = await api<{ customers: CustomerSummary[] }>(
        `/api/customers?q=${encodeURIComponent(query)}`
      );
      setRows(res.customers);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load customers.');
    }
  }, []);

  // Debounced so typing does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => void load(q), 220);
    return () => clearTimeout(t);
  }, [q, load]);

  return (
    <Screen width="wide">
      <Title sub="Customers">Mulaqaat</Title>

      <Row className="crumbs">
        <Link to="/" className="btn btn--ghost">
          ← Back
        </Link>
        <Button variant={adding ? 'ghost' : 'secondary'} onClick={() => setAdding((v) => !v)}>
          {adding ? 'Cancel' : 'Add customer'}
        </Button>
      </Row>

      {adding ? <AddCustomer onDone={() => { setAdding(false); void load(q); }} /> : null}

      <Card>
        <TextField
          label="Search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Name, city, or part of a number"
          autoComplete="off"
        />

        {error ? (
          <ErrorState message={error} onRetry={() => void load(q)} />
        ) : !rows ? (
          <Loading label="Loading customers…" />
        ) : rows.length === 0 ? (
          <EmptyState
            line={q ? `Nobody matches “${q}”.` : 'No customers yet.'}
            action={
              !adding ? (
                <Button onClick={() => setAdding(true)}>Add the first customer</Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="clist">
            {rows.map((c) => (
              <li key={c.id}>
                <Link to={`/customers/${c.id}`} className="clist__item">
                  <span className="clist__name">{c.name}</span>
                  <span className="clist__phone">{formatPhone(c.phone)}</span>
                  <span className="clist__meta">
                    {c.city ? <span className="clist__city">{c.city}</span> : null}
                    {c.family_label ? <Chip tone="gold">{c.family_label}</Chip> : null}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Screen>
  );
}

function AddCustomer({ onDone }: { onDone: () => void }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [timezone, setTimezone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dupe, setDupe] = useState<CustomerSummary | null>(null);
  const zones = useRef(timezoneOptions()).current;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setDupe(null);
    try {
      const res = await api<{ existing: boolean; customer: CustomerSummary }>('/api/customers', {
        method: 'POST',
        body: { name, phone, city: city || null, timezone: timezone || null },
      });
      if (res.existing) {
        // One number is one customer (§3) — say so plainly instead of duplicating.
        setDupe(res.customer);
        return;
      }
      onDone();
      navigate(`/customers/${res.customer.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add that customer.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <Heading>Add a customer</Heading>
      <form onSubmit={submit} noValidate>
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
        <TextField
          label="Phone number"
          type="tel"
          inputMode="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          hint="Any format. NRI numbers need their country code."
        />
        <TextField label="City (optional)" value={city} onChange={(e) => setCity(e.target.value)} maxLength={80} />

        <div className="field">
          <label className="field__label" htmlFor="tz-new">
            Their timezone (optional)
          </label>
          <select
            id="tz-new"
            className="field__input"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          >
            <option value="">Not set</option>
            {zones.map((z) => (
              <option key={z} value={z}>
                {z.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          <p className="field__hint">Used to show them times on their own clock.</p>
        </div>

        {dupe ? (
          <div className="dupe" role="alert">
            <p className="dupe__line">
              That number is already <strong>{dupe.name}</strong>.
            </p>
            <Link to={`/customers/${dupe.id}`} className="btn btn--secondary">
              Open their record
            </Link>
          </div>
        ) : null}

        {error ? (
          <p className="field__error" role="alert" style={{ marginBottom: 14 }}>
            {error}
          </p>
        ) : null}

        <Button type="submit" busy={busy} disabled={!name.trim() || !phone.trim()}>
          {busy ? 'Adding…' : 'Add customer'}
        </Button>
      </form>
    </Card>
  );
}
